import React, { useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { getProductImageURL, ProductImageSize } from '@noths/polaris-client-ribbons-design-system';
import { useImageLoadRef, useIntersection } from '@noths/polaris-client-utils';
import { Image } from '@noths/polaris-dev-ts-types';

import { TRANSITION_DURATION_IN_MS } from './constants';
import * as styles from './styles';

export type Props = Image & {
  alt?: string;
  size?: ProductImageSize;
  srcSet?: string;
  sizes?: string;
  disableLazyLoading?: boolean;
};

export const ProductCardImage = ({
  href,
  size,
  alt = '',
  srcSet,
  sizes,
  disableLazyLoading = false,
}: Props) => {
  const placeholderRef = useRef<HTMLImageElement | null>(null);
  const [imgRef, loaded] = useImageLoadRef();
  const [hasScrolledIntoView, setHasScrolledIntoView] = useState(false);

  useIntersection(() => setHasScrolledIntoView(true), placeholderRef, {
    rootMargin: '0px 0px 1000px 0px',
  });

  /*
    Prevent image from jarringly popping into view, by rendering a placeholder before the image has loaded.
    Render product placeholder initially and then only render the actual image when the placeholder has been scrolled into view. fade out placeholder & fade in image after it has finished loading.
  */
  return (
    <>
      <CSSTransition in={!loaded} timeout={TRANSITION_DURATION_IN_MS} unmountOnExit>
        <div
          data-testid="image-placeholder"
          ref={placeholderRef}
          css={[styles.image, styles.placeholder]}
          data-is-visible={!loaded}
        />
      </CSSTransition>
      <CSSTransition
        in={hasScrolledIntoView || disableLazyLoading}
        timeout={TRANSITION_DURATION_IN_MS}
        unmountOnExit
      >
        <img
          ref={imgRef}
          css={styles.image}
          alt={alt}
          src={getProductImageURL(href, size)}
          srcSet={srcSet}
          sizes={sizes}
          data-is-visible={loaded || disableLazyLoading}
        />
      </CSSTransition>
    </>
  );
};
